import React, { useState } from 'react';
import { getApiBaseUrl } from '../config/api';
import './AgentLogin.css';
import './AdminLogin.css';

const AdminLogin = ({ onLogin, onBackToMain }) => {
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordData, setForgotPasswordData] = useState({
    email: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);

  const API_BASE_URL = getApiBaseUrl();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    if (!credentials.email || !credentials.password) {
      setError('Email and password are required');
      setIsLoading(false);
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: credentials.email.trim(),
          password: credentials.password
        }),
      });
      if (!response.ok) {
        let errorMessage = 'Login failed. Please try again.';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        setError(errorMessage);
        setIsLoading(false);
        return;
      }
      const data = await response.json();
      if (data.success) {
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('adminUser', JSON.stringify(data.user));
        onLogin(data.user);
      } else {
        setError(data.error || 'Invalid email or password');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Login failed. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="agent-login-modern admin-login-modern">
      <div className="login-background">
        <div className="bg-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
        </div>
      </div>

      <div className="login-wrapper">
        <div className="login-card">
          <div className="login-header-modern">
            <div className="logo-container">
              <div className="logo-icon-modern">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <h1 className="login-title">Admin Portal</h1>
            <p className="login-subtitle">Welcome back. Sign in to the dashboard</p>
          </div>

          <form className="login-form-modern" onSubmit={handleSubmit}>
            <div className="input-group-modern">
              <div className={`input-wrapper ${focusedField === 'email' || credentials.email ? 'focused' : ''}`}>
                <label htmlFor="email" className="floating-label">
                  <span className="label-icon">✉️</span>
                  <span className="label-text">Email</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={credentials.email}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  placeholder=" "
                  required
                  disabled={isLoading}
                  autoComplete="email"
                  className="input-modern"
                />
                <div className="input-underline"></div>
              </div>
            </div>

            <div className="input-group-modern">
              <div className={`input-wrapper ${focusedField === 'password' || credentials.password ? 'focused' : ''}`}>
                <label htmlFor="password" className="floating-label">
                  <span className="label-icon">🔒</span>
                  <span className="label-text">Password</span>
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={credentials.password}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  placeholder=" "
                  required
                  disabled={isLoading}
                  autoComplete="current-password"
                  className="input-modern"
                />
                <button
                  type="button"
                  className="password-toggle-modern"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  )}
                </button>
                <div className="input-underline"></div>
              </div>
            </div>

            {error && (
              <div className="error-message-modern" role="alert">
                <svg className="error-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span className="error-text">{error}</span>
              </div>
            )}

            <div className="forgot-password-wrapper">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="forgot-password-link-modern"
                disabled={isLoading}
              >
                Forgot your password?
              </button>
            </div>

            <button
              type="submit"
              className="submit-button-modern"
              disabled={isLoading || !credentials.email || !credentials.password}
            >
              {isLoading ? (
                <>
                  <svg className="spinner-svg" viewBox="0 0 24 24">
                    <circle className="spinner-circle" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" strokeDasharray="32" strokeDashoffset="32">
                      <animate attributeName="stroke-dasharray" dur="2s" values="0 32;16 16;0 32;0 32" repeatCount="indefinite"/>
                      <animate attributeName="stroke-dashoffset" dur="2s" values="0;-16;-32;-32" repeatCount="indefinite"/>
                    </circle>
                  </svg>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <svg className="arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </>
              )}
            </button>
          </form>

          {onBackToMain && (
            <button
              className="back-to-main-modern"
              onClick={onBackToMain}
              disabled={isLoading}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              <span>Back to Main</span>
            </button>
          )}
        </div>
      </div>

      {showForgotPassword && (
        <div
          className="modal-overlay-modern"
          onClick={() => {
            if (!forgotPasswordLoading) {
              setShowForgotPassword(false);
              setForgotPasswordError('');
              setForgotPasswordSuccess(false);
              setForgotPasswordData({ email: '', newPassword: '', confirmPassword: '' });
            }
          }}
        >
          <div className="modal-content-modern" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-modern">
              <h2>Reset Password</h2>
              <button
                type="button"
                className="modal-close-modern"
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotPasswordError('');
                  setForgotPasswordSuccess(false);
                  setForgotPasswordData({ email: '', newPassword: '', confirmPassword: '' });
                }}
                disabled={forgotPasswordLoading}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {forgotPasswordSuccess ? (
              <div className="success-content-modern">
                <div className="success-icon-modern">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                </div>
                <h3>Password reset</h3>
                <p>Your password has been reset. You can now log in with your new password.</p>
                <button
                  type="button"
                  className="close-success-btn-modern"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordSuccess(false);
                    setForgotPasswordData({ email: '', newPassword: '', confirmPassword: '' });
                  }}
                >
                  Got it
                </button>
              </div>
            ) : (
              <form
                className="forgot-password-form-modern"
                onSubmit={async (e) => {
                  e.preventDefault();
                  setForgotPasswordLoading(true);
                  setForgotPasswordError('');
                  if (!forgotPasswordData.email) {
                    setForgotPasswordError('Email address is required');
                    setForgotPasswordLoading(false);
                    return;
                  }
                  if (!forgotPasswordData.newPassword || !forgotPasswordData.confirmPassword) {
                    setForgotPasswordError('Please enter and confirm your new password');
                    setForgotPasswordLoading(false);
                    return;
                  }
                  if (forgotPasswordData.newPassword !== forgotPasswordData.confirmPassword) {
                    setForgotPasswordError('Passwords do not match');
                    setForgotPasswordLoading(false);
                    return;
                  }
                  if (forgotPasswordData.newPassword.length < 6) {
                    setForgotPasswordError('Password must be at least 6 characters');
                    setForgotPasswordLoading(false);
                    return;
                  }
                  try {
                    const response = await fetch(`${API_BASE_URL}/api/admin/forgot-password`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        email: forgotPasswordData.email,
                        newPassword: forgotPasswordData.newPassword
                      }),
                    });
                    const data = await response.json();
                    if (response.ok && data.success) {
                      setForgotPasswordSuccess(true);
                      setForgotPasswordError('');
                    } else {
                      if (response.status === 404 || (data.error && data.error.toLowerCase().includes('not found'))) {
                        setForgotPasswordError('Email address not found. Please enter a registered admin email.');
                        setForgotPasswordData({ ...forgotPasswordData, newPassword: '', confirmPassword: '' });
                      } else {
                        setForgotPasswordError(data.error || 'Failed to reset password. Please try again.');
                      }
                    }
                  } catch (err) {
                    console.error('Forgot password error:', err);
                    setForgotPasswordError('Failed to reset password. Please try again.');
                  } finally {
                    setForgotPasswordLoading(false);
                  }
                }}
              >
                <div className="input-group-modern">
                  <div className={`input-wrapper ${forgotPasswordData.email ? 'focused' : ''}`}>
                    <label htmlFor="reset-email" className="floating-label">
                      <span className="label-icon">✉️</span>
                      <span className="label-text">Email Address</span>
                    </label>
                    <input
                      type="email"
                      id="reset-email"
                      value={forgotPasswordData.email}
                      onChange={(e) => setForgotPasswordData({ ...forgotPasswordData, email: e.target.value })}
                      placeholder=" "
                      required
                      disabled={forgotPasswordLoading}
                      autoComplete="email"
                      className="input-modern"
                    />
                    <div className="input-underline"></div>
                  </div>
                </div>
                <div className="input-group-modern">
                  <div className={`input-wrapper ${forgotPasswordData.newPassword ? 'focused' : ''}`}>
                    <label htmlFor="new-password" className="floating-label">
                      <span className="label-icon">🔒</span>
                      <span className="label-text">New Password</span>
                    </label>
                    <input
                      type="password"
                      id="new-password"
                      value={forgotPasswordData.newPassword}
                      onChange={(e) => setForgotPasswordData({ ...forgotPasswordData, newPassword: e.target.value })}
                      placeholder=" "
                      required
                      disabled={forgotPasswordLoading}
                      minLength={6}
                      className="input-modern"
                    />
                    <div className="input-underline"></div>
                  </div>
                </div>
                <div className="input-group-modern">
                  <div className={`input-wrapper ${forgotPasswordData.confirmPassword ? 'focused' : ''}`}>
                    <label htmlFor="confirm-password" className="floating-label">
                      <span className="label-icon">🔒</span>
                      <span className="label-text">Confirm Password</span>
                    </label>
                    <input
                      type="password"
                      id="confirm-password"
                      value={forgotPasswordData.confirmPassword}
                      onChange={(e) => setForgotPasswordData({ ...forgotPasswordData, confirmPassword: e.target.value })}
                      placeholder=" "
                      required
                      disabled={forgotPasswordLoading}
                      minLength={6}
                      className="input-modern"
                    />
                    <div className="input-underline"></div>
                  </div>
                </div>
                {forgotPasswordError && (
                  <div className="error-message-modern" role="alert">
                    <svg className="error-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <span className="error-text">{forgotPasswordError}</span>
                  </div>
                )}
                <div className="modal-actions-modern">
                  <button
                    type="button"
                    className="cancel-btn-modern"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordError('');
                      setForgotPasswordData({ email: '', newPassword: '', confirmPassword: '' });
                    }}
                    disabled={forgotPasswordLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-btn-modern"
                    disabled={forgotPasswordLoading || !forgotPasswordData.email || !forgotPasswordData.newPassword || !forgotPasswordData.confirmPassword}
                  >
                    {forgotPasswordLoading ? (
                      <>
                        <svg className="spinner-svg" viewBox="0 0 24 24">
                          <circle className="spinner-circle" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" strokeDasharray="32" strokeDashoffset="32">
                            <animate attributeName="stroke-dasharray" dur="2s" values="0 32;16 16;0 32;0 32" repeatCount="indefinite"/>
                            <animate attributeName="stroke-dashoffset" dur="2s" values="0;-16;-32;-32" repeatCount="indefinite"/>
                          </circle>
                        </svg>
                        <span>Resetting...</span>
                      </>
                    ) : (
                      <>
                        <span>Reset Password</span>
                        <svg className="arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M5 12h14M12 5l7 7-7 7"/>
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLogin;
